import { FilterExpression, like, Sort } from '@infuse/api/common';
import { useChargingStationTypeStore } from '@infuse/cpms/config/store';
import { FilterConfigurationType } from '@infuse/ui/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChargingStation } from './model';

export interface Options {
  query?: string;
  filter?: FilterExpression;
  page: number;
  pageSize: number;
  lastPage: boolean;
  sort?: Sort[];
  replaceInstallerAlias?: boolean;
  localTokenGroupId?: string;
  rootId?: string;
  domainOnly?: boolean;
  resetSort?: boolean;
  defaultSort?: Sort[];
  currentSearchId?: number;
  /** Ask the search for the filtered total (`matchingTotal`). Off by default — counting
   *  is extra cost; only enable where "select all N matching" needs N (the overview). */
  includeTotal?: boolean;
}

export interface State {
  items: ChargingStation[];
  item?: ChargingStation;
  options: Options;
  loading: boolean;
  counts: Record<string, number>;
  totalCount: number;
  /** Total stations matching the current query + filters (drives "select all N
   *  matching"). Set from the main search's `totalElements`, so it reflects the
   *  active filter — unlike `counts.total`, which is the unfiltered grand total. */
  matchingTotal: number;
  error: boolean;
}

// Escape backslash then apostrophe: umbrella string literals are single-quoted and use backslash
// escaping (not SQL doubling), so values with apostrophes (e.g. pool "Tim's locatie") don't 500 the parser.
export const escapeUmbrellaLikeValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

export const createSearchFilter = (
  searchQuery: string,
  filter: (part: string) => FilterExpression
): FilterExpression | undefined => {
  if (searchQuery === '') {
    return;
  }

  const filterOptions = searchQuery
    .toLowerCase()
    .split(' ')
    .filter((part: string): boolean => !!part)
    .map((part: string): string => `%${escapeUmbrellaLikeValue(part)}%`)
    .map((part: string): FilterExpression => filter(part));

  if (filterOptions.length === 0) {
    return;
  }

  return filterOptions.reduce((acc, cv) => acc.or(cv));
};

export const aggregateSearchQueryAndFilters = (options: Options): FilterExpression | undefined => {
  // combines regular filters with the search filter

  // Do not use the default search filter when we replace the aliases
  const searchFilter = options.replaceInstallerAlias
    ? undefined
    : createSearchFilter(options.query || '', (part) =>
        like('lower(id)', part).or(like('lower(pools.name)', part)).or(like('lower(customIdentifier)', part))
      );
  const filterCopy = new FilterExpression(); // copy is needed since FilterExpression methods alter the object itself
  filterCopy.operations = Object.assign([], options.filter?.operations); // shallow copy

  if (searchFilter) {
    return options.filter ? filterCopy.and(searchFilter) : searchFilter;
  } else {
    return options.filter;
  }
};
export function useChargingStationFilters() {
  const { t } = useI18n({ useScope: 'global' });
  const chargingStationTypeStore = useChargingStationTypeStore();

  const firmwareFilter = {
    name: 'firmware',
    type: FilterConfigurationType.QUERY,
    label: () => t('filters.firmware.label'),
    options: [
      {
        name: 'firmware',
        label: () => t('filters.firmware.placeholder'),
        toUmbrellaExpression(value: string): FilterExpression {
          return new FilterExpression()
            .like('attributes.value.firmwareVersion', `%${value}%`)
            .and(new FilterExpression().equals('attributes.key', 'firmwareVersion'));
        },
        toNegatedUmbrellaExpression(value: string): FilterExpression {
          return new FilterExpression()
            .notLike('attributes.value.firmwareVersion', `%${value}%`)
            .and(new FilterExpression().equals('attributes.key', 'firmwareVersion'));
        }
      }
    ]
  };

  const iccidFilter = {
    name: 'iccid',
    type: FilterConfigurationType.QUERY,
    label: () => t('filters.iccid.label'),
    options: [
      {
        name: 'iccid',
        label: () => t('filters.iccid.placeholder'),
        toUmbrellaExpression(value: string): FilterExpression {
          return new FilterExpression()
            .like('attributes.value.iccid', `%${value}%`)
            .and(new FilterExpression().equals('attributes.key', 'iccid'));
        },
        toNegatedUmbrellaExpression(value: string): FilterExpression {
          return new FilterExpression()
            .notLike('attributes.value.iccid', `%${value}%`)
            .and(new FilterExpression().equals('attributes.key', 'iccid'));
        }
      }
    ]
  };

  const showChargingStationVendor = import.meta.env.VITE_UI_VARIANT_SHOW_CHARGING_STATION_VENDOR !== 'false';

  const vendorAndModelFilter = computed(() => ({
    name: 'vendorAndModel',
    type: FilterConfigurationType.SELECT,
    label: () => showChargingStationVendor ? t('filters.vendorAndModel.label') : t('filters.vendorAndModel.labelModelOnly'),
    options: chargingStationTypeStore.items.map((item) => ({
      name: `${item.manufacturer.name}${item.type}`,
      label: () => showChargingStationVendor ? `${item.manufacturer.name} / ${item.type}` : item.type,
      toUmbrellaExpression(): FilterExpression {
        return new FilterExpression()
          .and(
            new FilterExpression()
              .equals('attributes.value.vendorId', item.manufacturer.name)
              .equals('attributes.key', 'vendorId')
          )
          .and(new FilterExpression().equals('attributes.value.model', item.type).equals('attributes.key', 'model'));
      },
      toNegatedUmbrellaExpression(): FilterExpression {
        return new FilterExpression()
          .and(
            new FilterExpression()
              .notEquals('attributes.value.vendorId', item.manufacturer.name)
              .equals('attributes.key', 'vendorId')
          )
          .or(new FilterExpression().notEquals('attributes.value.model', item.type).equals('attributes.key', 'model'));
      }
    }))
  }));

  return {
    firmwareFilter,
    iccidFilter,
    vendorAndModelFilter
  };
}
